import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatPhoneNumber } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft } from 'lucide-react';

interface AccountDetail {
  id: string;
  name: string;
  industry: string | null;
  website: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string;
  status: string;
  healthScore: number | null;
  annualRevenue: number | null;
  employeeCount: number | null;
  accountNumber: string | null;
}

export function AccountForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [status, setStatus] = useState('prospect');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const { data: accountResult } = useQuery({
    queryKey: ['account', id],
    queryFn: async () => {
      if (!id) return null;
      return api.getAccount(id);
    },
    enabled: isEditing,
  });
  const existingAccount = accountResult?.data as AccountDetail | undefined;

  useEffect(() => {
    if (existingAccount) {
      setName(existingAccount.name || '');
      setIndustry(existingAccount.industry || '');
      setWebsite(existingAccount.website || '');
      setPhone(existingAccount.phone || '');
      setAddressLine1(existingAccount.addressLine1 || '');
      setAddressLine2(existingAccount.addressLine2 || '');
      setCity(existingAccount.city || '');
      setState(existingAccount.state || '');
      setPostalCode(existingAccount.postalCode || '');
      setCountry(existingAccount.country || 'USA');
      setStatus(existingAccount.status || 'prospect');
      setAnnualRevenue(existingAccount.annualRevenue ? existingAccount.annualRevenue.toString() : '');
      setEmployeeCount(existingAccount.employeeCount ? existingAccount.employeeCount.toString() : '');
      setAccountNumber(existingAccount.accountNumber || '');
    }
  }, [existingAccount]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createAccount(data as Parameters<typeof api.createAccount>[0]),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      const account = result.data as { id: string } | undefined;
      if (account?.id) {
        navigate(`/customer-operations/accounts/${account.id}`);
      } else {
        navigate('/customer-operations/accounts');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateAccount(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      navigate(`/customer-operations/accounts/${id}`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      name,
      industry: industry || null,
      website: website || null,
      phone: phone || null,
      addressLine1: addressLine1 || null,
      addressLine2: addressLine2 || null,
      city: city || null,
      state: state || null,
      postalCode: postalCode || null,
      country: country || 'USA',
      accountNumber: accountNumber || null,
    };

    if (isEditing) {
      payload.status = status;
      payload.annualRevenue = annualRevenue ? parseFloat(annualRevenue) : null;
      payload.employeeCount = employeeCount ? parseInt(employeeCount) : null;
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Edit Account' : 'New Account'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <Label>Account Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Company name"
                  required
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Auto-generated (e.g. ACC-1001)"
                />
              </div>
              <div>
                <Label>Industry</Label>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g. Technology, Healthcare"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Website</Label>
                <Input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  placeholder="xxx-xxx-xxxx"
                  maxLength={12}
                />
              </div>
            </div>

            {isEditing && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="churned">Churned</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Annual Revenue</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={annualRevenue}
                    onChange={(e) => setAnnualRevenue(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Employees</Label>
                  <Input
                    type="number"
                    min="0"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Address Line 1</Label>
              <Input
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="Street address"
              />
            </div>
            <div>
              <Label>Address Line 2</Label>
              <Input
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder="Suite, floor, etc."
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </div>
              <div>
                <Label>Zip Code</Label>
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || !name}>
            {isSaving ? 'Saving...' : isEditing ? 'Update Account' : 'Create Account'}
          </Button>
        </div>
      </form>
    </div>
  );
}
